import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Check, AlertCircle, Copy } from "lucide-react";
import api from "@/lib/api";
import { filterActiveVisibleTemplates } from "@/lib/workflowFilters";

// Convert ISO string or Date to yyyy-mm-dd for <input type=date>
const toDateInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};
const dateInputToIso = (val) => (val ? new Date(val + "T00:00:00Z").toISOString() : null);

const Field = ({ label, hint, required, children }) => (
  <label className="block">
    <span className="text-[11.5px] font-semibold text-[#6B7280] uppercase tracking-wider">
      {label}
      {required && <span className="text-[#EF4444] ml-1">*</span>}
    </span>
    <div className="mt-2">{children}</div>
    {hint && <p className="mt-1 text-[11px] text-[#9CA3AF]">{hint}</p>}
  </label>
);

const inputClass =
  "w-full h-11 rounded-2xl border border-[#EEF2F5] bg-white px-4 text-[14px] text-[#111111] placeholder:text-[#9CA3AF] outline-none focus:border-[#111111] transition-colors";

const DossierForm = ({ mode = "create", initial = null, onClose, onSaved }) => {
  const isEdit = mode === "edit";

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);

  const [clientName, setClientName] = useState(initial?.client || "");
  const [clientEmail, setClientEmail] = useState(initial?.clientEmail || "");
  const [clientPhone, setClientPhone] = useState(initial?.clientPhone || "");

  const [vehicleName, setVehicleName] = useState(initial?.name || "");
  const [vehicleImage, setVehicleImage] = useState(initial?.image || "");
  const [orderNumber, setOrderNumber] = useState(initial?.orderNumber || "");
  const [availabilityDate, setAvailabilityDate] = useState(toDateInput(initial?.availabilityRaw));

  const [deliveryDate, setDeliveryDate] = useState(toDateInput(initial?.deliveryDate));
  const [deliveryTime, setDeliveryTime] = useState(initial?.deliveryTime || "");
  const [deliveryLocation, setDeliveryLocation] = useState(initial?.deliveryLocation || "");
  const [commercialId, setCommercialId] = useState(initial?.commercialId || "");
  const [workflowTemplates, setWorkflowTemplates] = useState([]);
  const [workflowTemplateId, setWorkflowTemplateId] = useState("");

  const [createdDossier, setCreatedDossier] = useState(null);
  const [magicLink, setMagicLink] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.listAgencyUsers().then(setUsers).catch(() => setUsers([]));
    api.listWorkflowTemplates().then((tpls) => {
      // Sprint V1 · Point 9 : filtre les snap_/del_/test_wf_ et les archivés.
      const active = filterActiveVisibleTemplates(tpls);
      setWorkflowTemplates(active);
      const def = active.find((t) => t.is_default) || active[0];
      if (def && !workflowTemplateId) setWorkflowTemplateId(def.id);
    }).catch(() => setWorkflowTemplates([]));
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  const canSubmit =
    clientName.trim() &&
    vehicleName.trim() &&
    orderNumber.trim() &&
    availabilityDate;

  const handleSubmit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    setError(null);
    try {
      const commercial = users.find((u) => u.id === commercialId);
      const payload = {
        order_number: orderNumber.trim().toUpperCase(),
        vehicle_name: vehicleName.trim(),
        vehicle_image: vehicleImage.trim() || null,
        client: {
          name: clientName.trim(),
          email: clientEmail.trim() || null,
          phone: clientPhone.trim() || null,
        },
        commercial_id: commercialId || null,
        commercial_name: commercial?.name || null,
        workflow_template_id: workflowTemplateId || null,
        availability_date: dateInputToIso(availabilityDate),
        delivery_date: dateInputToIso(deliveryDate),
        delivery_time: deliveryTime || null,
        delivery_location: deliveryLocation.trim() || null,
      };

      let saved;
      if (isEdit) {
        saved = await api.updateDossier(initial.id, payload);
        onSaved?.(saved);
        onClose();
      } else {
        saved = await api.createDossier(payload);
        setCreatedDossier(saved);
        try {
          const magic = await api.createMagicLink(saved.id);
          setMagicLink(`${window.location.origin}${magic.path}`);
        } catch { /* magic link optional */ }
        setStep(3);
      }
    } catch (e) {
      setError(e.message || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const copyMagic = async () => {
    if (!magicLink) return;
    try {
      await navigator.clipboard.writeText(magicLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard denied */ }
  };

  const finish = () => {
    onSaved?.(createdDossier);
    onClose();
  };

  const title = isEdit ? "Modifier le dossier" : "Nouveau dossier";
  const subtitle = isEdit
    ? "Mettez à jour les informations du dossier."
    : step === 3
      ? "Le dossier est créé. Partagez le lien avec votre client."
      : "3 étapes rapides pour créer un dossier client.";

  const drawer = (
    <div className="fixed inset-0 z-[100]" data-testid="dossier-form">
      <div
        onClick={onClose}
        className="absolute inset-0 bg-[#111111]/25 backdrop-blur-[2px] animate-[fade-in_.2s_ease-out]"
      />
      <aside
        className="absolute inset-y-0 right-0 w-full sm:w-[540px] bg-white shadow-[0_20px_60px_-20px_rgba(17,17,17,0.25)] flex flex-col animate-[slide-in-right_.3s_cubic-bezier(0.22,1,0.36,1)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-[#EEF2F5]">
          <div className="flex-1 min-w-0 pr-4">
            <p className="text-[11px] font-semibold text-[#22C55E] uppercase tracking-wider">
              {isEdit ? "Édition" : "Création"}
            </p>
            <h3 className="mt-2 text-[22px] font-semibold text-[#111111] tracking-tight">
              {title}
            </h3>
            <p className="mt-1 text-[12.5px] text-[#6B7280] leading-snug">
              {subtitle}
            </p>
            {!isEdit && step < 3 && (
              <div className="mt-4 flex items-center gap-1.5" data-testid="form-stepper">
                {[1, 2, 3].map((n) => (
                  <span
                    key={n}
                    className={[
                      "h-1.5 flex-1 rounded-full transition-colors",
                      n <= step ? "bg-[#111111]" : "bg-[#EEF2F5]",
                    ].join(" ")}
                  />
                ))}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="dossier-form-close"
            className="h-9 w-9 rounded-full hover:bg-[#F7F8FA] flex items-center justify-center text-[#6B7280]"
            aria-label="Fermer"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5" data-testid="form-body-scroll">
          {/* STEP 3 = confirmation (create only) */}
          {!isEdit && step === 3 && createdDossier ? (
            <div className="space-y-6 py-4">
              <div className="mx-auto h-14 w-14 rounded-full bg-[#DCFCE7] text-[#166534] flex items-center justify-center">
                <Check size={22} strokeWidth={2.5} />
              </div>
              <div className="text-center">
                <p className="text-[16px] font-semibold text-[#111111]">
                  Dossier créé
                </p>
                <p className="mt-1 text-[13px] text-[#6B7280]">
                  {createdDossier.order_number} · {createdDossier.vehicle_name}
                </p>
              </div>
              <div className="rounded-2xl border border-[#EEF2F5] bg-[#F7F8FA] p-4">
                <p className="text-[10.5px] font-semibold text-[#6B7280] uppercase tracking-wider">
                  Code client généré
                </p>
                <p className="mt-2 text-[22px] font-semibold tracking-widest text-[#111111] tabular-nums">
                  {createdDossier.access_code}
                </p>
                <p className="mt-3 text-[11.5px] text-[#6B7280] leading-relaxed">
                  À communiquer à votre client avec son numéro de bon de commande.
                </p>
              </div>

              {magicLink && (
                <div className="rounded-2xl border border-[#EEF2F5] p-4">
                  <p className="text-[10.5px] font-semibold text-[#6B7280] uppercase tracking-wider">
                    Lien magique (accès direct)
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <p className="flex-1 text-[12px] text-[#111111] font-mono truncate">
                      {magicLink}
                    </p>
                    <button
                      data-testid="copy-magic-link"
                      onClick={copyMagic}
                      className="h-9 inline-flex items-center gap-1.5 rounded-full bg-[#111111] text-white text-[12px] font-medium px-3 hover:bg-black transition-colors"
                    >
                      {copied ? (
                        <>
                          <Check size={12} strokeWidth={2.5} />
                          Copié
                        </>
                      ) : (
                        <>
                          <Copy size={12} strokeWidth={2} />
                          Copier
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              <button
                data-testid="dossier-created-open"
                onClick={finish}
                className="w-full h-12 rounded-full bg-[#111111] text-white text-[14px] font-semibold hover:bg-black transition-colors"
              >
                Ouvrir le dossier
              </button>
            </div>
          ) : (
            <>
              {(isEdit || step === 1) && (
                <div className="space-y-5" data-testid="form-step-client">
                  <p className="text-[13.5px] font-semibold text-[#111111]">Client</p>
                  <Field label="Nom complet" required>
                    <input
                      data-testid="form-client-name"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="Jean Dupont"
                      className={inputClass}
                    />
                  </Field>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Email">
                      <input
                        data-testid="form-client-email"
                        type="email"
                        value={clientEmail}
                        onChange={(e) => setClientEmail(e.target.value)}
                        placeholder="jean@exemple.fr"
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Téléphone">
                      <input
                        data-testid="form-client-phone"
                        value={clientPhone}
                        onChange={(e) => setClientPhone(e.target.value)}
                        placeholder="+33 6 XX XX XX XX"
                        className={inputClass}
                      />
                    </Field>
                  </div>
                </div>
              )}

              {(isEdit || step === 2) && (
                <div className="space-y-5" data-testid="form-step-vehicle">
                  {isEdit && <div className="border-t border-[#EEF2F5]" />}
                  <p className="text-[13.5px] font-semibold text-[#111111]">Véhicule & bon de commande</p>
                  {!isEdit && workflowTemplates.length > 0 && (
                    <Field label="Type de vente" hint="Le workflow détermine les étapes du dossier.">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {workflowTemplates.slice(0, 6).map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => setWorkflowTemplateId(t.id)}
                            data-testid={`workflow-${t.slug}`}
                            className={[
                              "text-left rounded-2xl border p-3 transition-all",
                              workflowTemplateId === t.id
                                ? "border-[#111111] bg-[#F7F8FA] ring-1 ring-[#111111]/10"
                                : "border-[#EEF2F5] bg-white hover:border-[#111111]/25",
                            ].join(" ")}
                          >
                            <p className="text-[13px] font-semibold text-[#111111]">{t.name}</p>
                            <p className="text-[11px] text-[#6B7280] mt-0.5 line-clamp-2">
                              {t.description || `${t.steps?.length || 0} étapes`}
                            </p>
                            <p className="mt-2 text-[10.5px] font-medium text-[#6B7280] uppercase tracking-wider">
                              {t.steps?.length || 0} étapes
                            </p>
                          </button>
                        ))}
                      </div>
                    </Field>
                  )}
                  <Field label="Nom du véhicule" required hint="Marque · Modèle · Finition">
                    <input
                      data-testid="form-vehicle-name"
                      value={vehicleName}
                      onChange={(e) => setVehicleName(e.target.value)}
                      placeholder="Audi RS3 Sportback"
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Image (URL)" hint="Photo temporaire — utilisez ensuite l'onglet Médias pour upload.">
                    <input
                      data-testid="form-vehicle-image"
                      value={vehicleImage}
                      onChange={(e) => setVehicleImage(e.target.value)}
                      placeholder="https://..."
                      className={inputClass}
                    />
                  </Field>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Bon de commande" required hint="Ex: BC-2026-0300">
                      <input
                        data-testid="form-order-number"
                        value={orderNumber}
                        onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
                        placeholder="BC-2026-XXXX"
                        className={inputClass + " uppercase"}
                        disabled={isEdit}
                      />
                    </Field>
                    <Field label="Disponibilité estimée" required>
                      <input
                        data-testid="form-availability"
                        type="date"
                        value={availabilityDate}
                        onChange={(e) => setAvailabilityDate(e.target.value)}
                        className={inputClass}
                      />
                    </Field>
                  </div>
                </div>
              )}

              {(isEdit || step === 2) && (
                <div className="space-y-5">
                  <div className="border-t border-[#EEF2F5]" />
                  <p className="text-[13.5px] font-semibold text-[#111111]">Livraison & commercial</p>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Date livraison">
                      <input
                        data-testid="form-delivery-date"
                        type="date"
                        value={deliveryDate}
                        onChange={(e) => setDeliveryDate(e.target.value)}
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Heure">
                      <input
                        data-testid="form-delivery-time"
                        type="time"
                        value={deliveryTime}
                        onChange={(e) => setDeliveryTime(e.target.value)}
                        className={inputClass}
                      />
                    </Field>
                  </div>
                  <Field label="Lieu">
                    <input
                      data-testid="form-delivery-location"
                      value={deliveryLocation}
                      onChange={(e) => setDeliveryLocation(e.target.value)}
                      placeholder="Agence · 125 av. de la République, Toulon"
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Commercial référent">
                    <select
                      data-testid="form-commercial-id"
                      value={commercialId}
                      onChange={(e) => setCommercialId(e.target.value)}
                      className={inputClass + " appearance-none"}
                    >
                      <option value="">— Aucun</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                  </Field>
                </div>
              )}
            </>
          )}

          {error && (
            <div
              data-testid="form-error"
              className="rounded-2xl bg-[#FEE2E2] border border-[#FECACA] px-4 py-3 flex items-start gap-2"
            >
              <AlertCircle size={14} strokeWidth={2} className="text-[#991B1B] mt-0.5 shrink-0" />
              <p className="text-[13px] text-[#991B1B] leading-snug">{error}</p>
            </div>
          )}
        </div>

        {(isEdit || step < 3) && (
          <div className="p-4 border-t border-[#EEF2F5] flex items-center gap-2">
            {!isEdit && step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                data-testid="form-back"
                className="h-11 px-4 rounded-full bg-white border border-[#EEF2F5] text-[13px] font-medium text-[#111111] hover:bg-[#F7F8FA]"
              >
                Retour
              </button>
            )}
            <div className="flex-1" />
            {!isEdit && step < 2 && (
              <button
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && !clientName.trim()}
                data-testid="form-next"
                className="h-11 px-5 rounded-full bg-[#111111] text-white text-[13px] font-semibold hover:bg-black disabled:opacity-40"
              >
                Continuer
              </button>
            )}
            {(isEdit || step === 2) && (
              <button
                onClick={handleSubmit}
                disabled={!canSubmit || saving}
                data-testid="form-submit"
                className="h-11 px-5 rounded-full bg-[#111111] text-white text-[13px] font-semibold hover:bg-black transition-colors disabled:opacity-40 inline-flex items-center gap-2"
              >
                {saving && <Loader2 size={13} strokeWidth={2} className="animate-spin-slow" />}
                {isEdit ? "Enregistrer" : "Créer le dossier"}
              </button>
            )}
          </div>
        )}
      </aside>
    </div>
  );
  return createPortal(drawer, document.body);
};

export default DossierForm;
